import { useState } from 'react';

import CustomModal from '@/components/CustomModal';
import DatePicker from '@/components/DatePicker';
import Select from '@/components/Select';
import { DeletedRecordTypeOptions } from '@/constants/constant';
import { ERestoreEntityType } from '@/enum';
import { useFormatText } from '@/hooks/useFormatText';

interface IFilterValues {
  type?: ERestoreEntityType[];
  start_date?: number | null;
  end_date?: number | null;
}

const DeletedRecordsFilterModal = ({
  setIsOpen,
  onApply,
  initialValues,
}: {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  onApply: (filters: IFilterValues) => void;
  initialValues?: IFilterValues;
}) => {
  const { formatText } = useFormatText();
  const [type, setType] = useState<ERestoreEntityType[] | undefined>(
    initialValues?.type
  );
  const [startDate, setStartDate] = useState<string | null>(
    initialValues?.start_date
      ? new Date(initialValues.start_date * 1000).toISOString()
      : null
  );
  const [endDate, setEndDate] = useState<string | null>(
    initialValues?.end_date
      ? new Date(initialValues.end_date * 1000).toISOString()
      : null
  );

  const handleClear = () => {
    setType(undefined);
    setStartDate(null);
    setEndDate(null);
    onApply({});
    setIsOpen(false);
  };

  const handleApply = () => {
    onApply({
      type,
      start_date: startDate
        ? Math.floor(new Date(startDate).getTime() / 1000)
        : undefined,
      end_date: endDate
        ? Math.floor(new Date(endDate).getTime() / 1000)
        : undefined,
    });
    setIsOpen(false);
  };

  return (
    <CustomModal
      setIsOpen={setIsOpen}
      isHeader
      isFooter
      heading={formatText('common.actions.filter')}
      position="right"
      className="max-w-150"
      confirmButtonProps={{
        label: formatText('common.actions.show-results'),
        variant: 'primary',
        onClick: handleApply,
      }}
      cancelButtonProps={{
        label: formatText('common.actions.clear-filters'),
        desructive: true,
        onClick: handleClear,
        className: 'text-sm underline px-0!',
      }}
    >
      <div className="flex flex-col gap-6 w-full">
        {/* Type Filter */}
        <div>
          <Select
            name={formatText('settings.deleted-records.filters.type')}
            options={DeletedRecordTypeOptions}
            selected={type ? type?.map(String) : []}
            onChange={(val) =>
              setType(
                val
                  ? ((val as string[])?.map(Number) as ERestoreEntityType[])
                  : undefined
              )
            }
            placeholder={formatText(
              'settings.deleted-records.filters.type-placeholder'
            )}
            className="w-full"
            isClearable
            isTextFormat
            isMulti
          />
        </div>

        {/* Start Date Filter */}
        <div>
          <DatePicker
            label={formatText('settings.deleted-records.filters.start-date')}
            placeholder={formatText(
              'settings.deleted-records.filters.start-date-placeholder'
            )}
            value={startDate}
            onChange={setStartDate}
          />
        </div>

        {/* End Date Filter */}
        <div>
          <DatePicker
            label={formatText('settings.deleted-records.filters.end-date')}
            placeholder={formatText(
              'settings.deleted-records.filters.end-date-placeholder'
            )}
            value={endDate}
            onChange={setEndDate}
          />
        </div>
      </div>
    </CustomModal>
  );
};

export default DeletedRecordsFilterModal;
